import type { Contact, Currency, Passenger } from '../../api/types/common';
import type { ShoppingCart } from '../../api/types/shoppingCart';
import type { CurrentAccount } from '../../api/types/reports';
import type { BillingInformation } from '../../api/types/billing';
import { submitBilling } from '../../api/endpoints/billing';
import { redirectToBankIfNeeded } from '../../components/payment/paymentRedirect';
import { cartIsPaid, type PersistedBookingFlowConfig } from '../../booking-flow/resume';
import { hotelPersistConfig } from './resume';
import {
  allocateHotel,
  makeHotelReservation,
  makePayment,
  searchHotels,
  updateHotelPassengers,
} from '../../api/endpoints/hotel';
import type {
  HotelAllocateRequest,
  HotelBooking,
  HotelMakeReservationResponse,
  HotelPassengerDetail,
  HotelSearchRequest,
  HotelSearchResponse,
  PaymentForm,
  PaymentResponse,
  PaymentType,
  PriceResult,
} from '../../api/types/hotel';

/**
 * Otel booking-flow state — migration guide Bölüm 6.1: Search → Allocate →
 * UpdatePassengers → MakeReservation (Hotel'in FinalizeShopping karşılığı; the sandbox
 * exposes no separate hotel prebooking endpoint, so this step is skipped as noted in
 * the task brief) → MakePayment → Document. Each step's response feeds refs into the
 * next request — shoppingCartId flows from Allocate through everything after it.
 */
export interface HotelFlowState {
  searchRequest?: HotelSearchRequest;
  searchResult?: HotelSearchResponse;
  /** One `PriceResult` IS one bookable hotel+room+rate combination — see
   *  api/types/hotel.ts. Replaces the old selectedHotel/selectedRoom pair. */
  selectedOffer?: PriceResult;
  shoppingCart?: ShoppingCart | null;
  shoppingCartId?: string;
  guests: Passenger[];
  contact: Contact;
  /** Cari hesap (Report/FindCurrentAccount) — seçilirse Billing/Submit'e onun vergi
   *  no'suyla kurumsal fatura olarak gönderilir, air modülüyle aynı desen. */
  selectedCurrentAccount?: CurrentAccount;
  /** Misafir bilgisi formunda derlenir, UpdatePassengers başarılı olduktan hemen sonra
   *  Billing/Submit'e gönderilir — air modülündeki sırayla aynı. */
  billingInformation?: BillingInformation;
  reservation?: HotelMakeReservationResponse;
  /** Kurumsal bütçe/politika dışı bir rezervasyon misafir adımı "Onaya Gönder" ile
   *  tamamlandı (UpdatePassengers → Billing/Submit, sonra yöneticinin onay listesine düşer).
   *  `HotelFlowPage` bunu görünce bilgi verip rezervasyon listesine döner. Transient. */
  sentForApproval?: boolean;
  paymentForm?: PaymentForm;
  /** 'CurrentPayment' seçiliyse `paymentForm` gerekmiyor — cari hesap zaten
   *  Billing/Submit ile sepete bağlanmış oluyor (bkz. `runUpdatePassengers`), air
   *  modülündeki sırayla aynı. */
  paymentType?: PaymentType;
  payment?: PaymentResponse;
}

/** Reads the freshly-allocated cart's hotel line item, falling back to the originally
 *  selected search-result offer when the cart doesn't carry it (HotelBooking's exact
 *  field-by-field shape is unconfirmed — see comment in api/types/hotel.ts). */
function resolveBookingFacts(state: HotelFlowState) {
  const booking = state.shoppingCart?.hotelBookings?.[0] as HotelBooking | undefined;
  // Confirmed via a live Allocate response: `ArkmanHotelID` comes back as the NUMBER `0`
  // for non-Arkman-catalog hotels (e.g. RateHawk-sourced, `ArkmanHotel: false`) — a
  // legitimate value the backend's validator accepts, NOT a missing one. `String(...)`
  // turns it into `"0"` so the `!arkmanHotelId` guard below doesn't misread it as absent
  // (raw `0` is falsy in JS; `"0"` isn't).
  const arkmanHotelId = String(booking?.arkmanHotelID ?? state.selectedOffer?.hotelId ?? '');
  const startDate = booking?.startDate ?? state.searchRequest?.startDate ?? '';
  const endDate = booking?.endDate ?? state.searchRequest?.endDate ?? '';
  const currencyType: Currency =
    (booking?.currency as Currency | undefined) ??
    state.selectedOffer?.currencyType ??
    state.searchRequest?.currencyType ??
    'TRY';
  const arkmanPrice =
    booking?.totalFare ??
    booking?.totalFareTRY ??
    state.selectedOffer?.arkmanPrice ??
    state.selectedOffer?.servicePrice ??
    0;
  return { arkmanHotelId, startDate, endDate, currencyType, arkmanPrice };
}

/** One guest "slot" the search's room breakdown expects to be filled, in order:
 *  room 1's adults, then room 1's children, then room 2's adults, ... `paxType`
 *  confirmed via a live Hotel/UpdatePassenger request: 0 = Yetişkin (adult),
 *  1 = Çocuk (child). */
export interface HotelGuestSlot {
  roomId: number;
  paxType: 0 | 1;
}

/** Falls back to a single adult slot in room 1 when the search has no room
 *  breakdown yet (e.g. flow state not fully hydrated). */
export function buildHotelGuestSlots(searchRequest?: HotelSearchRequest): HotelGuestSlot[] {
  const rooms = searchRequest?.passengersInfo ?? [];
  const slots: HotelGuestSlot[] = [];
  rooms.forEach((room, roomIndex) => {
    const roomId = room.roomId ?? roomIndex + 1;
    for (let i = 0; i < room.adt; i += 1) slots.push({ roomId, paxType: 0 });
    for (let i = 0; i < room.child; i += 1) slots.push({ roomId, paxType: 1 });
  });
  return slots.length ? slots : [{ roomId: 1, paxType: 0 }];
}

/** "1. Oda - 1. Yetişkin", "1. Oda - 1. Çocuk", "2. Oda - 1. Yetişkin", ... — per-room,
 *  per-type numbering so multi-room/adult+child bookings stay unambiguous in the UI.
 *  Shared between `GuestsStep` (form labels) and `ReservationStep` (summary). */
export function buildHotelGuestLabels(slots: HotelGuestSlot[]): string[] {
  const counters = new Map<string, number>();
  return slots.map((slot) => {
    const key = `${slot.roomId}-${slot.paxType}`;
    const n = (counters.get(key) ?? 0) + 1;
    counters.set(key, n);
    return `${slot.roomId}. Oda - ${n}. ${slot.paxType === 1 ? 'Çocuk' : 'Yetişkin'}`;
  });
}

/** Zips the flat guest list (built by `GuestsStep` in the same room/adult-then-child
 *  order as `buildHotelGuestSlots`) with each slot's room number + pax type — Swagger's
 *  `PassengerDetail` carries a `paxRoomNo`/`paxType` per guest that the shared
 *  `Passenger` shape has no equivalent for. Unlike Air/Transfer, a live Hotel/Allocate
 *  response confirmed `PassengerDetails`/`PaxDetails` come back `null` — the backend does
 *  NOT pre-create per-pax slots with a server-generated id to correlate against, so
 *  `paxReferanceId` is generated client-side here instead (kept stable per guest object
 *  across resubmits via `guest.paxReferanceId` when already present). */
function buildPassengerDetails(guests: Passenger[], searchRequest?: HotelSearchRequest): HotelPassengerDetail[] {
  const slots = buildHotelGuestSlots(searchRequest);
  return guests.map((guest, index) => {
    const slot = slots[index] ?? slots[slots.length - 1];
    return {
      name: guest.firstName ?? '',
      surName: guest.lastName ?? '',
      paxType: slot.paxType,
      paxRoomNo: slot.roomId,
      gender: guest.gender,
      nationalId: guest.citizenNo,
      birthDate: guest.birthDate,
      hesCode: guest.hesCode,
      paxReferanceId: guest.paxReferanceId ?? crypto.randomUUID(),
    };
  });
}

async function runSearch(state: HotelFlowState): Promise<Partial<HotelFlowState>> {
  if (!state.searchRequest) throw new Error('Arama kriterleri eksik.');
  const searchResult = await searchHotels(state.searchRequest);
  return { searchResult };
}

async function runAllocate(state: HotelFlowState): Promise<Partial<HotelFlowState>> {
  const offer = state.selectedOffer;
  const rateId = offer?.rateId;
  if (!rateId) throw new Error('Bir oda/fiyat seçmelisiniz.');
  const request: HotelAllocateRequest = {
    rateId,
    rateName: offer?.rateName,
    offerId: offer?.offerId,
    hotelId: offer?.hotelId,
    roomId: offer?.rooms?.[0]?.roomId,
    roomName: offer?.roomName,
    arkmanPrice: offer?.arkmanPrice ?? offer?.servicePrice,
    currencyType: offer?.currencyType,
    cancellationPolicy: offer?.cancellationPolicy,
    searchId: offer?.searchId ?? state.searchRequest?.searchId,
    arkmanHotel: offer?.arkmanHotel,
    language: state.searchRequest?.language,
    startDate: state.searchRequest?.startDate,
    endDate: state.searchRequest?.endDate,
    locationKey: state.searchRequest?.locationKey,
    passengersInfo: state.searchRequest?.passengersInfo,
    employeeID: state.searchRequest?.employeeID,
  };
  const res = await allocateHotel(request);
  return { shoppingCart: res.shoppingCart, shoppingCartId: res.shoppingCart?.id };
}

async function runUpdatePassengers(state: HotelFlowState): Promise<Partial<HotelFlowState>> {
  if (!state.shoppingCartId) throw new Error('Sepet bulunamadı.');
  if (!state.guests.length) throw new Error('En az bir misafir bilgisi girmelisiniz.');
  if (!state.billingInformation) throw new Error('Fatura bilgisi eksik.');
  const billingInformation = state.billingInformation;
  const passengers = buildPassengerDetails(state.guests, state.searchRequest);
  const res = await updateHotelPassengers(state.shoppingCartId, passengers, {
    contact: state.contact,
    hotelName: state.selectedOffer?.hotelName,
  });
  const shoppingCart = res.shoppingCart ?? state.shoppingCart;
  if (!shoppingCart?.id) return shoppingCart ? { shoppingCart } : {};
  const billingRes = await submitBilling({ shoppingCartId: shoppingCart.id, billingInformation });
  return { shoppingCart: billingRes.shoppingCart ?? shoppingCart };
}

async function runMakeReservation(state: HotelFlowState): Promise<Partial<HotelFlowState>> {
  const { arkmanHotelId, startDate, endDate, currencyType, arkmanPrice } = resolveBookingFacts(state);
  if (!arkmanHotelId) throw new Error('Otel bilgisi bulunamadı.');
  const reservation = await makeHotelReservation({
    shoppingCartId: state.shoppingCartId,
    startDate,
    endDate,
    currencyType,
    arkmanPrice,
    arkmanHotelId,
    surName: state.contact.lastName ?? '',
    name: state.contact.name ?? '',
    phone: state.contact.phone ?? '',
    mail: state.contact.email ?? '',
    rateId: state.selectedOffer?.rateId,
    offerId: state.selectedOffer?.offerId,
    serviceHotelID: state.selectedOffer?.hotelId,
    servicePrice: state.selectedOffer?.servicePrice,
  });
  return { reservation };
}

async function runPayment(state: HotelFlowState): Promise<Partial<HotelFlowState>> {
  if (!state.shoppingCartId) throw new Error('Sepet bulunamadı.');
  // Yenilenmiş/geri dönülmüş bir akışta ödenmiş sepete ikinci kez ödeme denenmesin.
  if (cartIsPaid(state.shoppingCart)) throw new Error('Bu rezervasyon zaten ödenmiş.');
  const paymentType = state.paymentType ?? 'CreditCardPayment';
  if (paymentType !== 'CurrentPayment' && !state.paymentForm) {
    throw new Error('Ödeme bilgileri eksik.');
  }
  const { arkmanPrice } = resolveBookingFacts(state);
  const payment = await makePayment({
    shoppingCartId: state.shoppingCartId,
    amount: state.shoppingCart?.remainingTotal ?? arkmanPrice,
    paymentType,
    // Cari (CurrentPayment): kart bilgisi yok — hesap zaten Billing/Submit'te sepete
    // bağlandı, sadece paymentType ile hangi yöntemin seçildiği bildiriliyor.
    paymentForm: paymentType === 'CurrentPayment' ? undefined : state.paymentForm,
  });
  // 3D Secure: banka onay URL'i geldiyse tarayıcı bankaya yönlendirilir; dönüşte
  // /payment/return sonucu doğrular (bkz. components/payment/paymentRedirect.ts).
  if (redirectToBankIfNeeded(payment)) return new Promise<Partial<HotelFlowState>>(() => {});
  return { payment };
}

/** Terminal step — voucher/document display only, no further API call. */
async function runDocument(): Promise<Partial<HotelFlowState>> {
  return {};
}

export const hotelFlowConfig: PersistedBookingFlowConfig<HotelFlowState> = {
  id: 'hotel',
  initialState: { guests: [], contact: {} },
  persist: hotelPersistConfig,
  steps: [
    { id: 'search', label: 'Arama', run: runSearch },
    { id: 'allocate', label: 'Oda Seçimi', run: runAllocate },
    { id: 'guests', label: 'Misafir Bilgileri', run: runUpdatePassengers },
    { id: 'reservation', label: 'Rezervasyon', run: runMakeReservation },
    { id: 'payment', label: 'Ödeme', run: runPayment },
    { id: 'document', label: 'Voucher', run: runDocument },
  ],
};
